import React, { useState, useRef, useEffect } from 'react';
import type { FC, MouseEvent as ReactMouseEvent } from 'react';
import ReactDOM from 'react-dom';

import { Button, ButtonSize, ButtonType } from '@/common/button';
import { Modal } from '@/common/modal';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import { CheckIcon } from './check-icon';
import { UncheckIcon } from './uncheck-icon';
import { MENU_Z_INDEX } from './z-index';

interface ExportCreativeModalProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    exportCount: number;
    onConfirm: (formats: string[]) => void;
}

export const ExportCreativeModal: FC<ExportCreativeModalProps> = ({ isOpen, setIsOpen, onConfirm, exportCount }) => {
    const exportFormats = [
        { name: 'PNG', value: 'png' },
        { name: 'JPEG', value: 'jpeg' },
    ];
    const [isSelectOpen, setIsSelectOpen] = useState<boolean>(false);
    const [selectedFormats, setSelectedFormats] = useState<string[]>([]);

    const ref = useRef<HTMLDivElement>(null);
    const parentRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const toggleMenu = (e: ReactMouseEvent) => {
        e.stopPropagation();
        setIsSelectOpen((prev) => !prev);
    };

    const toggleSelect = (value: string) => {
        setSelectedFormats((prev) => {
            if (prev.includes(value)) {
                return prev.filter((item) => item !== value);
            }
            return [...prev, value];
        });
    };

    const getPosition = () => {
        const rect = ref.current?.getBoundingClientRect();
        return {
            top: (rect?.bottom ?? 0) + 10, // add space
            left: rect?.left,
        };
    };

    useEffect(() => {
        if (!isOpen) setIsSelectOpen(false);
    }, [isOpen]);

    return (
        <Modal isOpen={isOpen}>
            <div className="flex flex-col absolute w-[40vw] bg-gray-150 shadow-xl rounded-xl transition-all duration-300 ease-in-out">
                <div className="flex flex-row p-3 justify-between items-center">
                    <p className="text-xl text-white">Select the file format for export</p>
                    <XMarkIcon
                        className="h-5 w-5 text-white hover:cursor-pointer hover:text-gray-30"
                        onClick={() => setIsOpen(false)}
                    />
                </div>
                <hr className="bg-gray-100 border-gray-100 w-full" />
                <div className="flex flex-row items-center p-4 w-full" ref={parentRef}>
                    <div className="relative inline-block w-full" ref={ref}>
                        <div className="flex flex-col gap-2">
                            <p className="text-white text-sm">Export Format</p>
                            <button type="button" className="w-full" onClick={toggleMenu}>
                                <div className="flex flex-row justify-between items-center w-full p-3 bg-gray-100 rounded-md">
                                    {selectedFormats.length === 0 ? (
                                        <p className="text-sm text-gray-50">Click to select file type</p>
                                    ) : (
                                        <div className="flex flex-row gap-2">
                                            {selectedFormats.map((item, index) => {
                                                const format = exportFormats.find((format) => format.value === item);
                                                return (
                                                    <div
                                                        key={item}
                                                        className="flex flex-row justify-between text-sm text-white bg-gray-80 py-1 px-2 rounded w-24"
                                                    >
                                                        {format?.name}
                                                        <button
                                                            type="button"
                                                            className="flex items-center"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleSelect(item);
                                                            }}
                                                        >
                                                            <XMarkIcon className="h-5 w-5 text-white hover:cursor-pointer hover:text-gray-30" />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <ChevronDownIcon className="h-4 w-4 text-white" />
                                </div>
                            </button>
                        </div>
                        {isSelectOpen &&
                            ReactDOM.createPortal(
                                <div
                                    className="absolute bg-gray-100 shadow-xl rounded-md transition-all duration-100 ease-in-out max-h-[200px] overflow-y-scroll opacity-100 visible p-1"
                                    style={{
                                        zIndex: MENU_Z_INDEX,
                                        ...getPosition(),
                                        position: 'absolute',
                                        width: 'calc(40vw - 32px)',
                                    }}
                                    ref={menuRef}
                                >
                                    {exportFormats.map((item) => {
                                        const isSelected = selectedFormats.includes(item.value);
                                        return (
                                            <button
                                                type="button"
                                                key={item.value}
                                                className="flex flex-row gap-1.5 w-full px-2 py-3 border-gray-120 border-b-[1px] text-white text-gray-700 hover:bg-gray-80 rounded-md text-xs text-left items-center"
                                                onClick={(e: ReactMouseEvent) => {
                                                    e.stopPropagation();
                                                    toggleMenu(e);
                                                    toggleSelect(item.value);
                                                }}
                                            >
                                                {isSelected ? <CheckIcon /> : <UncheckIcon />}
                                                {item.name}
                                            </button>
                                        );
                                    })}
                                </div>,
                                document.body,
                            )}
                    </div>
                </div>
                <hr className="bg-gray-100 border-gray-100 w-full mt-2" />
                <div className="w-full flex flex-row justify-end gap-3 p-6">
                    <Button
                        title="Cancel"
                        buttonType={ButtonType.SECONDARY}
                        buttonSize={ButtonSize.LARGE}
                        onClick={() => setIsOpen(false)}
                    />
                    <Button
                        title={`Export ${exportCount} Asset${exportCount > 1 ? 's' : ''}`}
                        buttonType={ButtonType.PRIMARY}
                        buttonSize={ButtonSize.LARGE}
                        onClick={async () => {
                            setIsOpen(false);
                            onConfirm(selectedFormats);
                        }}
                    />
                </div>
            </div>
        </Modal>
    );
};
